import axios, { type AxiosInstance } from "axios";

export type StaffBrandUser = {
  email: string;
  firstName: string | null;
  lastName: string | null;
};

export type OnboardedBrand = {
  id: string;
  name: string;
  avatarImageUrl: string | null;
  runningCampaignCount: number;
  owner: { email: string } | null;
  users: Array<{ email: string }>;
};

export type BrandUserListItem = {
  email: string;
  isOwner: boolean;
  brands: Array<{
    id: string;
    name: string;
    avatarImageUrl: string | null;
  }>;
};

export type BrandUserListPage = {
  users: BrandUserListItem[];
  hasMore: boolean;
};

export type ListBrandUsersInput = {
  query?: string;
  page?: number;
  limit?: number;
};

export type AssignBrandUserInput = {
  brandId: string;
  email: string;
  firstName?: string;
  lastName?: string;
};

export type AssignBrandUserResult = {
  brandId: string;
  email: string;
  status: "assigned" | "invited";
};

export class StaffBrandUsersService {
  constructor(private readonly http: AxiosInstance) {}

  async listOnboardedBrands(): Promise<OnboardedBrand[]> {
    const response = await this.http.get<{ brands: OnboardedBrand[] }>(
      "/staff/brands",
    );
    return response.data.brands;
  }

  async listUsers(input: ListBrandUsersInput = {}): Promise<BrandUserListPage> {
    const response = await this.http.get<BrandUserListPage>("/staff/users", {
      params: input,
    });
    return response.data;
  }

  async findByEmail(email: string): Promise<StaffBrandUser | null> {
    try {
      const response = await this.http.get<StaffBrandUser>(
        "/staff/users/by-email",
        { params: { email: email.trim().toLowerCase() } },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async assign(input: AssignBrandUserInput): Promise<AssignBrandUserResult> {
    const { brandId, ...body } = input;
    const response = await this.http.put<AssignBrandUserResult>(
      `/staff/brands/${encodeURIComponent(brandId)}/users`,
      body,
    );
    return response.data;
  }
}
